import React, { useState, useMemo } from 'react'
import { Search, Plus, Briefcase, Eye, Edit, Trash2, MoreHorizontal, Users } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectOption,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Pagination,
} from '../components/ui'
import { LoadingSpinner, LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import { usePositions, type Position, type PositionFilters } from '../hooks/usePositions'
import { useDepartments } from '../hooks/useDepartments'
import { useLatestSync } from '../hooks/useSyncStatus'
import { PositionForm } from '../components/positions/PositionForm'
import { DeletePositionDialog } from '../components/positions/DeletePositionDialog'

export function Positions() {
  const [filters, setFilters] = useState<PositionFilters>({
    page: 1,
    limit: 20,
    sort: 'name:asc',
  })

  const [searchTerm, setSearchTerm] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState<string>('')

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null)

  // Debounced search
  const debouncedFilters = useMemo(() => {
    const timer = setTimeout(() => {
      return {
        ...filters,
        search: searchTerm || undefined,
        department_id: departmentFilter || undefined,
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [filters, searchTerm, departmentFilter])

  const { data, isLoading, error, refetch } = usePositions(filters)
  const { data: departmentsData } = useDepartments({ limit: 100 }) // Для фильтра по подразделениям
  const { data: syncData } = useLatestSync()

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setFilters(prev => ({ ...prev, page: 1, search: e.target.value || undefined }))
  }

  const handleDepartmentFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setDepartmentFilter(value)
    setFilters(prev => ({
      ...prev,
      page: 1,
      department_id: value || undefined
    }))
  }

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters(prev => ({ ...prev, sort: e.target.value }))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatSyncTime = (syncData: any) => {
    if (!syncData?.last_sync_at) {
      return 'Нет данных'
    }

    const syncTime = new Date(syncData.last_sync_at)
    const now = new Date()
    const diffMs = now.getTime() - syncTime.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMinutes < 1) {
      return 'Только что'
    } else if (diffMinutes < 60) {
      return `${diffMinutes} мин назад`
    } else if (diffHours < 24) {
      return `${diffHours} ч назад`
    } else if (diffDays < 7) {
      return `${diffDays} дн назад`
    } else {
      return formatDate(syncData.last_sync_at)
    }
  }

  const getSourceBadge = (position: Position) => {
    if (position.code_1c) {
      return <Badge variant="secondary">1С</Badge>
    }
    return <Badge variant="outline">TCO</Badge>
  }

  const getEmployeeCount = (position: Position) => {
    return position.employees_count ?? position.employees?.length ?? 0
  }

  // Action handlers
  const handleEdit = (position: Position) => {
    setSelectedPosition(position)
    setIsEditModalOpen(true)
  }

  const handleDelete = (position: Position) => {
    setSelectedPosition(position)
    setIsDeleteModalOpen(true)
  }

  const handleView = (position: Position) => {
    // TODO: Implement view modal or navigate to detail page
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Должности</h1>
          <p className="text-muted-foreground">
            Управление должностями сотрудников организации
          </p>
        </div>
        <ErrorState
          title="Ошибка загрузки должностей"
          onAction={refetch}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Должности</h1>
          <p className="text-muted-foreground">
            Управление должностями сотрудников организации
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Добавить должность
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Фильтры и поиск</CardTitle>
          <CardDescription>
            Используйте фильтры для поиска нужных должностей
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск по названию..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10"
              />
            </div>

            {/* Department filter */}
            <Select
              placeholder="Подразделение"
              value={departmentFilter}
              onChange={handleDepartmentFilter}
            >
              <SelectOption value="">Все подразделения</SelectOption>
              {departmentsData?.data.map((dept) => (
                <SelectOption key={dept.id} value={dept.id}>
                  {dept.name}
                </SelectOption>
              ))}
            </Select>

            {/* Sort */}
            <Select
              value={filters.sort}
              onChange={handleSortChange}
            >
              <SelectOption value="name:asc">По названию (А-Я)</SelectOption>
              <SelectOption value="name:desc">По названию (Я-А)</SelectOption>
              <SelectOption value="created_at:desc">По дате создания (новые)</SelectOption>
              <SelectOption value="created_at:asc">По дате создания (старые)</SelectOption>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Briefcase className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{data.total}</p>
                  <p className="text-sm text-muted-foreground">Всего должностей</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Badge variant="secondary" className="mr-1">1С</Badge>
                <div>
                  <p className="text-2xl font-bold">
                    {data.from1cCount}
                  </p>
                  <p className="text-sm text-muted-foreground">Из 1С</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Badge variant="outline" className="mr-1">TCO</Badge>
                <div>
                  <p className="text-2xl font-bold">
                    {data.localCount}
                  </p>
                  <p className="text-sm text-muted-foreground">Локальные</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Eye className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Последняя синхронизация</p>
                  <p className="text-sm font-medium">
                    {formatSyncTime(syncData)}
                  </p>
                  {syncData?.last_sync_type && (
                    <p className="text-xs text-muted-foreground">
                      Тип: {syncData.last_sync_type}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Список должностей</CardTitle>
          <CardDescription>
            {data && `Показано ${data.data.length} из ${data.total} должностей`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={filters.limit || 20} />
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Название</TableHead>
                    <TableHead>Организация</TableHead>
                    <TableHead>Подразделение</TableHead>
                    <TableHead>Сотрудники</TableHead>
                    <TableHead>Источник</TableHead>
                    <TableHead>Дата создания</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((position) => (
                    <TableRow key={position.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium">{position.name}</p>
                          {position.code_1c && (
                            <p className="text-sm text-muted-foreground">
                              Код 1С: {position.code_1c}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {position.organizations ? (
                          <span className="text-sm">{position.organizations.name}</span>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {position.departments ? (
                          <div>
                            <p className="font-medium">
                              {position.departments.name}
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Не указано</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{getEmployeeCount(position)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getSourceBadge(position)}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {formatDate(position.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleView(position)}
                            title="Просмотр"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(position)}
                            title="Редактировать"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(position)}
                            title={getEmployeeCount(position) > 0
                              ? `Нельзя удалить: ${getEmployeeCount(position)} сотрудник(ов)`
                              : "Удалить"}
                            className={getEmployeeCount(position) > 0 ? "opacity-50" : ""}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Дополнительные действия"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Briefcase className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                Должности не найдены
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Попробуйте изменить фильтры поиска или добавьте новую должность.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <PositionForm
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        mode="create"
      />

      <PositionForm
        open={isEditModalOpen}
        onOpenChange={(open) => {
          setIsEditModalOpen(open)
          if (!open) {
            setSelectedPosition(null)
          }
        }}
        position={selectedPosition}
        mode="edit"
      />

      <DeletePositionDialog
        open={isDeleteModalOpen}
        onOpenChange={(open) => {
          setIsDeleteModalOpen(open)
          if (!open) {
            setSelectedPosition(null)
          }
        }}
        position={selectedPosition}
      />
    </div>
  )
}